import React from 'react';
import InfiniteScroll from '../../InfiniteScroll';
import PropTypes from 'prop-types';
import { css, cx } from 'react-emotion';
import styled from '@emotion/styled';
import SvgIcon from 'react-icons-kit/SvgIcon';
import { infoCircle } from 'react-icons-kit/fa/infoCircle';
import {
  Alert,
  Button,
  Form,
  FormControl,
  Modal,
  OverlayTrigger,
  Table,
  Tooltip,
} from 'react-bootstrap';
import SortableColumnHeaders from '../../SortableColumnHeaders';
import canDo from '../../../utils/canDo';
import ErrorAlert from '../../ErrorAlert';
import { Container } from '@amzn/et-polaris-utils';
import Spinner from '../../Spinner';
import PriceSchemeDialog from './PriceSchemeDialog';
import { Link, Redirect } from 'react-router-dom';
import SettingsButtons from '../SettingsButtons';

const IconDiv = styled('div')`
  padding-left: 6px;
  display: inline-block;
`;

const columns = [
  {
    title: 'Name',
    sortKey: 'name',
  },
  {
    /* Empty column for action buttons */
    title: '',
    sortKey: null,
  },
];

export class Pricing extends React.Component {
  static propTypes = {
    onLoadMore: PropTypes.func,
    onReload: PropTypes.func,
    onSearch: PropTypes.func,
    onSortChange: PropTypes.func,
    onCreateEvent: PropTypes.func,
    onUpdateEvent: PropTypes.func,
    onRemoveEvent: PropTypes.func,
    session: PropTypes.object.isRequired,
    priceSchemes: PropTypes.object.isRequired,
    features: PropTypes.object,
  };

  static defaultProps = {
    onLoadMore: Function.prototype,
    onReload: Function.prototype,
    onSearch: Function.prototype,
    onSortChange: Function.prototype,
    onCreateEvent: Function.prototype,
    onUpdateEvent: Function.prototype,
    onRemoveEvent: Function.prototype,
    session: Object.prototype,
    priceSchemes: Object.prototype,
    features: Object.prototype,
  };

  constructor(props) {
    super(props);
    this.state = {
      showAddModal: false,
      showDeleteModal: false,
      priceSchemeToDelete: null,
      lastDeleteError: null,
      currentPriceScheme: null,
      searchPattern: '',
      shouldRedirect: false,
    };

    this.handleOpenAddModal = this.handleOpenAddModal.bind(this);
    this.handleCloseAddModal = this.handleCloseAddModal.bind(this);

    this.handleSearch = this.handleSearch.bind(this);
    this.updateSearchPattern = this.updateSearchPattern.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.props.onReload(true);
  }

  handleOpenAddModal() {
    this.setState({ showAddModal: true });
  }

  handleCloseAddModal(priceScheme) {
    this.setState({
      showAddModal: false,
      currentPriceScheme: priceScheme,
      shouldRedirect: priceScheme !== undefined,
    });
  }

  handleSearch(e) {
    e.preventDefault();
    const { searchPattern } = this.state;
    this.props.onSearch(searchPattern);
  }

  updateSearchPattern(e) {
    this.setState({ searchPattern: e.target.value });
  }

  render() {
    const handleShowDeleteConfirmModal = priceScheme => {
      this.setState({
        priceSchemeToDelete: priceScheme,
        lastDeleteError: null,
      });
    };

    const handleCloseDeleteConfirmModal = () => {
      this.setState({ priceSchemeToDelete: null });
    };

    const handleDoDeleteAndClose = () => {
      this.props
        .onRemoveEvent(this.state.priceSchemeToDelete)
        .then(() => {
          handleCloseDeleteConfirmModal();
        })
        .catch(e => {
          this.setState({ lastDeleteError: e });
        });
    };

    const {
      items,
      isFetching,
      lastError,
      sortOrder,
      sortField,
      fullyLoaded,
    } = this.props.priceSchemes;
    const { features, onReload } = this.props;
    const { priceSchemeToDelete, currentPriceScheme, lastDeleteError, shouldRedirect } = this.state;

    // After the user has created a new price and the dialog closes, we need
    // to send them to the price scheme detail page.
    if (shouldRedirect) {
      return <Redirect to={`/settings/pricing/${currentPriceScheme.id}`} />;
    }

    return (
      <Container
        title="Price Schemes"
        rawButtons={
          <SettingsButtons
            writePermission={canDo(this, 'pricing:write')}
            onCreate={this.handleOpenAddModal}
            onReload={onReload}
            createTitle="Add Price Scheme"
            isFetching={isFetching}
          />
        }
      >
        <InfiniteScroll
          onInfiniteScroll={this.props.onLoadMore}
          isLoading={isFetching}
          fullyLoaded={fullyLoaded}
          fireOnChange={items.length}
        />

        <Modal show={this.state.showAddModal} onHide={this.handleCloseAddModal}>
          <PriceSchemeDialog
            closeHandler={this.handleCloseAddModal}
            onUpdateEvent={this.props.onCreateEvent}
            priceScheme={{ basePrices: [] }}
            title={'Add Price Scheme'}
          />
        </Modal>
        {priceSchemeToDelete != null && (
          <Modal show={priceSchemeToDelete != null} onHide={handleCloseDeleteConfirmModal}>
            <Modal.Header closeButton>
              <Modal.Title>Delete Price Scheme?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {lastDeleteError && (
                <ErrorAlert title="Failed to delete price scheme" errorInfo={lastDeleteError} />
              )}
              Do you really want to remove the {"'"}
              {priceSchemeToDelete.name}
              {"'"} price scheme?
            </Modal.Body>
            <Modal.Footer>
              <Button id="cancel-delete" onClick={handleCloseDeleteConfirmModal}>
                Cancel
              </Button>
              <Button bsStyle="primary" onClick={handleDoDeleteAndClose}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {// FIXME: was this ever turned on?
        features.pricingSearch && (
          <Form
            inline
            id="searchForm"
            onSubmit={e => this.handleSearch(e)}
            css={css`
              padding-bottom: 1.5rem;
            `}
          >
            <FormControl
              onChange={e => this.updateSearchPattern(e)}
              value={this.state.searchPattern}
            />{' '}
            <Button type="submit" className="btn-sm" disabled={isFetching}>
              Search
            </Button>
          </Form>
        )}

        {lastError && <ErrorAlert title="Failed to load price schemes" errorInfo={lastError} />}

        {!lastError && !isFetching && items.length === 0 && (
          <Alert bsStyle="info">No price schemes found.</Alert>
        )}

        {items.length > 0 && (
          <Table striped condensed hover>
            <SortableColumnHeaders
              columns={columns}
              sortField={sortField}
              sortOrder={sortOrder}
              onSortChanged={this.props.onSortChange}
            />
            <tbody>
              {items.map(priceScheme => {
                return (
                  <tr key={priceScheme.id}>
                    <td>
                      {priceScheme.name}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip">{priceScheme.description}</Tooltip>}
                      >
                        <IconDiv>
                          <SvgIcon icon={infoCircle} size={16} />
                        </IconDiv>
                      </OverlayTrigger>
                    </td>
                    <td className="row-actions">
                      {canDo(this.props.session, 'pricing:write') && (
                        <React.Fragment>
                          <Link
                            className="btn btn-xs btn-default"
                            to={`/settings/pricing/${priceScheme.id}`}
                          >
                            Edit
                          </Link>
                          <button
                            className={cx('btn', 'btn-xs', 'btn-danger')}
                            onClick={() => handleShowDeleteConfirmModal(priceScheme)}
                          >
                            Delete
                          </button>
                        </React.Fragment>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {isFetching && (
          <div
            css={css`
              text-align: center;
            `}
          >
            <Spinner size={30} />
          </div>
        )}
      </Container>
    );
  }
}

export default Pricing;
